<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
    <!-- Overlay -->
    <div class="absolute inset-0" @click="$emit('cancelModal')"></div>

    <!-- Modal Container -->
    <div class="relative w-full max-w-lg sm:max-w-2xl h-screen overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-700">
        <h2 id="modal-title" class="text-xl font-semibold text-indigo-300">แก้ไขชั้นใต้ดิน: {{ editedBasement.name || 'ไม่มีชื่อ' }}</h2>
        <button @click="$emit('cancelModal')" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700 bg-gray-800">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="switchTab(tab.id)"
          class="flex-1 px-4 py-3 text-sm font-medium text-white transition duration-200"
          :class="{
            'bg-indigo-600 border-b-2 border-indigo-500': currentTab === tab.id,
            'bg-gray-600 hover:bg-gray-500': currentTab !== tab.id
          }"
        >
          {{ tab.name }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700">
        <form @submit.prevent="saveChanges">
          <!-- Basic Info Tab -->
          <div v-if="currentTab === 'basic'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 bg-gray-800 py-2">ข้อมูลพื้นฐาน</h3>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อชั้น (เช่น b1, b2) <span class="text-red-500">*</span></label>
                <input
                  v-model="editedBasement.name"
                  type="text"
                  placeholder="เช่น b1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลิงก์รูปภาพ <span class="text-red-500">*</span></label>
                <input
                  v-model="editedBasement.imageUrl"
                  type="url"
                  placeholder="https://example.com/basement.jpg"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <div v-if="editedBasement.imageUrl" class="mt-2">
                  <img
                    :src="editedBasement.imageUrl"
                    alt="Basement Preview"
                    class="w-24 h-24 object-cover rounded-lg"
                    @error="editedBasement.imageUrl = ''"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เลเวลที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedBasement.requiredLevel"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนห้องที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedBasement.roomRequired"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">พลังงานที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedBasement.requiredEnergy"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลดความสุข <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedBasement.reduceHappy"
                  type="number"
                  min="0"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">อนุญาตให้ค้นหาผู้เล่นคนอื่น</label>
                <input
                  v-model="editedBasement.allowSearchOtherPlayers"
                  type="checkbox"
                  class="w-5 h-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                />
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">ค่าเข้า</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เหรียญ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.requiredCoins"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">ทอง <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.requiredGold"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เพชร <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.requiredDiamonds"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">แคช <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.requiredCash"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">การสูญเสีย</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนไอเทมในกระเป๋าที่เสียขั้นต่ำ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.minBagItemsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนไอเทมในกระเป๋าที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.maxBagItemsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนทักษะที่เสียขั้นต่ำ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.minSkillsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนทักษะที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.maxSkillsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนรอยสักที่เสียขั้นต่ำ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.minTattoosToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนรอยสักที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.maxTattoosToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนไอเทมที่ใส่อยู่ที่เสียขั้นต่ำ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.minEquippedItemsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนไอเทมที่ใส่อยู่ที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.maxEquippedItemsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนห้องที่เสียขั้นต่ำ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.minRoomsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนห้องที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.maxRoomsToLose"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">คูลดาวน์</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">คูลดาวน์การค้นหา (วินาที) <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.searchCooldown"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">คูลดาวน์การค้นหาผู้เล่นอื่น (วินาที) <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.searchOtherPlayersCooldown"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">คูลดาวน์การประมวลผลการต่อสู้ (วินาที) <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.combatProcessingCooldown"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">คูลดาวน์การออกจากชั้นใต้ดิน (วินาที) <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.exitBasementCooldown"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">โอกาสการพบ</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสพบอาชญากร (%) <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.criminalEncounterChance"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสพบผู้เล่นอื่น (%) <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.otherPlayerEncounterChance"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">พลังงานและระยะเวลา</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">ระยะเวลาหมดอายุของอีเวนต์ (วินาที) <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.eventExpirationTime"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">พลังงานที่ใช้สำหรับการค้นหา <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.searchEnergyCost"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">พลังงานที่ใช้สำหรับค้นหาผู้เล่นอื่น <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="editedBasement.searchOtherPlayersEnergyCost"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Criminals Tab -->
          <div v-if="currentTab === 'criminals'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-200 bg-gray-800 py-2">อาชญากรในชั้นใต้ดิน</h3>
            <div class="flex gap-3 mb-3">
              <button
                @click="toggleAllCriminals"
                type="button"
                class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              >
                {{ criminalShowDetailsAll ? 'ไม่แสดงทั้งหมด' : 'แสดงทั้งหมด' }}
              </button>
            </div>
            <div class="space-y-3">
              <div
                v-for="(criminal, index) in editedBasement.criminals"
                :key="index"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div v-if="criminalLoadingStates[index]" class="flex justify-center items-center py-4">
                  <div class="loader"></div>
                </div>
                <div v-else-if="criminalShowDetailsAll && criminalDetails[criminal.name]" class="mb-4 text-gray-300 text-sm bg-glass p-4 rounded-lg flex items-start gap-4">
                  <img
                    v-if="criminalDetails[criminal.name]?.imageUrl"
                    :src="criminalDetails[criminal.name].imageUrl"
                    :alt="criminal.name"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div class="space-y-2">
                    <div>
                      <span class="font-bold">เลเวล:</span> {{ criminalDetails[criminal.name]?.level || 0 }}
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-6 sm:col-span-7">
                    <input
                      v-model="criminal.name"
                      type="text"
                      placeholder="ชื่ออาชญากร (เช่น Thief)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-4 sm:col-span-3">
                    <input
                      v-model.number="criminal.chance"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="%"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      v-if="index > 0"
                      @click="removeCriminal(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบอาชญากร"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addCriminal"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มอาชญากร
                </button>
                <button
                  @click="normalizeChances"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  คำนวณโอกาส
                </button>
              </div>
              <div v-if="totalChance > 0" class="text-sm text-gray-400 mt-2">
                <p>โอกาสการเจออาชญากร (รวม: {{ totalChance.toFixed(2) }}%):</p>
                <p v-if="remainingChance > 0" class="text-yellow-500">เหลือ: {{ remainingChance.toFixed(2) }}%</p>
                <p v-else-if="remainingChance < 0" class="text-red-500">เกิน: {{ Math.abs(remainingChance).toFixed(2) }}%</p>
                <ul class="list-disc list-inside">
                  <li v-for="(criminal, index) in editedBasement.criminals" :key="index">
                    {{ criminal.name || 'ยังไม่ระบุ' }}: {{ criminal.chance || 0 }}%
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Rewards Tab -->
          <div v-if="currentTab === 'rewards'" class="space-y-4 animate-fadeIn">
            <h3 class="text-lg font-medium text-gray-300 bg-gray-800 py-2">รางวัลเมื่อสำเร็จ</h3>
            <h4 class="text-md font-medium text-gray-300 mt-4">รางวัลเงินตรา</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เหรียญเงิน (Silver) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedBasement.rewardSilver"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ทอง (Gold) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedBasement.rewardGold"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เพชร (Diamond) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="editedBasement.rewardDiamond"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนครั้งที่ต้องค้นหาเพื่อได้รับรางวัลเงินตรา <span class="text-red-500">*</span></label>
              <input
                v-model.number="editedBasement.requiredSearchesForCurrency"
                type="number"
                min="1"
                placeholder="1"
                class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                required
                aria-required="true"
              />
            </div>
            <h4 class="text-md font-medium text-gray-300 mt-4">รางวัลไอเทม</h4>
            <div class="flex items-center gap-2 mb-3">
              <label for="itemTypeFilter" class="text-gray-300 text-sm font-medium">ประเภทไอเทม:</label>
              <select
                id="itemTypeFilter"
                v-model="selectedItemType"
                class="bg-gray-700 text-white border border-gray-600 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">ทั้งหมด</option>
                <option value="wearable">ไอเทมสวมใส่</option>
                <option value="book">หนังสือ</option>
                <option value="general">ทั่วไป</option>
                <option value="food">อาหาร</option>
                <option value="map">แผนที่</option>
                <option value="heal">รักษา</option>
              </select>
            </div>
            <div class="flex gap-3 mb-3">
              <button
                @click="toggleAllRewards"
                type="button"
                class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              >
                {{ rewardShowDetailsAll ? 'ไม่แสดงทั้งหมด' : 'แสดงทั้งหมด' }}
              </button>
            </div>
            <div class="bg-gray-900 bg-opacity-50 p-4 rounded-lg text-sm text-gray-300">
              <p><span class="font-bold">จำนวนไอเทมประเภทสวมใส่:</span> {{ wearableItemsCount }} ชิ้น</p>
              <p><span class="font-bold">จำนวนไอเทมประเภททั่วไป:</span> {{ generalItemsCount }} ชิ้น</p>
              <p><span class="font-bold">จำนวนรางวัลทั้งหมด:</span> {{ filteredRewards.length }} ชิ้น</p>
            </div>
            <div class="space-y-3">
              <div
                v-for="(reward, index) in filteredRewards"
                :key="index"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
                :class="itemDetails[reward.itemName]?.rarityColors?.borderColor"
              >
                <div v-if="loadingStates[index]" class="flex justify-center items-center py-4">
                  <div class="loader"></div>
                </div>
                <div v-else-if="rewardShowDetailsAll && itemDetails[reward.itemName]" class="mb-4 text-gray-300 text-sm bg-glass p-4 rounded-lg flex items-start gap-4">
                  <img
                    v-if="itemDetails[reward.itemName]?.img"
                    :src="itemDetails[reward.itemName].img"
                    :alt="reward.itemName"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div class="space-y-2">
                    <div>
                      <span class="font-bold">ประเภท:</span> {{ itemDetails[reward.itemName]?.type || 'N/A' }}
                      <span>(ราคา: {{ itemDetails[reward.itemName]?.price || 0 }})</span>
                    </div>
                    <div>
                      <span class="font-bold">ความหายาก:</span> {{ getStars(itemDetails[reward.itemName]?.rarity) }}
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-6 sm:col-span-7">
                    <input
                      v-model="reward.itemName"
                      type="text"
                      placeholder="ชื่ออุปกรณ์ (เช่น กระสุน)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-4 sm:col-span-3">
                    <input
                      v-model.number="reward.dropChance"
                      type="number"
                      min="0"
                      max="10000"
                      placeholder="จาก 10,000"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      @click="removeReward(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบรางวัล"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mb-3">
                <button
                  @click="addReward"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มรางวัล
                </button>
                <button
                  @click="sortRewards"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  จัดเรียงรางวัล
                </button>
              </div>
            </div>
            <p v-if="filteredRewards.length === 0" class="text-gray-300 text-sm">ไม่มีรางวัลที่ตรงกับตัวกรอง</p>
          </div>
        </form>
      </div>

      <!-- Back to Top Button -->
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="fixed bottom-20 right-6 px-4 py-2 bg-indigo-600 rounded-lg text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200"
        aria-label="กลับไปด้านบน"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
        </svg>
      </button>

      <!-- Fixed Action Buttons -->
      <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
        <button
          @click="deleteBasement"
          type="button"
          class="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
        >
          ลบชั้นใต้ดิน
        </button>
        <div class="flex gap-3">
          <button
            @click="$emit('cancelModal')"
            type="button"
            class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-200"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            @click="saveChanges"
            class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
            :disabled="totalChance !== 100"
          >
            บันทึก
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFirestore } from '@/firebase/config';

export default {
  props: { basement: { type: Object, required: true } },
  emits: ['cancelModal', 'basementUpdated', 'basementDeleted'],
  setup(props, { emit }) {
    const editedBasement = ref({
      ...props.basement,
      requiredLevel: props.basement.requiredLevel || 1,
      roomRequired: props.basement.roomRequired || 1,
      requiredEnergy: props.basement.requiredEnergy || 0,
      reduceHappy: props.basement.reduceHappy || 1,
      requiredCoins: props.basement.requiredCoins || 0,
      requiredGold: props.basement.requiredGold || 0,
      requiredDiamonds: props.basement.requiredDiamonds || 0,
      requiredCash: props.basement.requiredCash || 0,
      criminals: props.basement.criminals ? [...props.basement.criminals] : [{ name: '', chance: 0 }],
      rewards: props.basement.rewards ? [...props.basement.rewards] : [{ itemName: '', dropChance: 0 }],
      itemsToLoseCount: props.basement.itemsToLoseCount || 0,
      skillsToLoseCount: props.basement.skillsToLoseCount || 0,
      tattoosToLoseCount: props.basement.tattoosToLoseCount || 0,
      bagItemsToLoseCount: props.basement.bagItemsToLoseCount || 0,
      rewardSilver: props.basement.rewardSilver || 0,
      rewardGold: props.basement.rewardGold || 0,
      rewardDiamond: props.basement.rewardDiamond || 0,
      allowSearchOtherPlayers: props.basement.allowSearchOtherPlayers || false,
      minBagItemsToLose: props.basement.minBagItemsToLose || 0,
      maxBagItemsToLose: props.basement.maxBagItemsToLose || 0,
      minSkillsToLose: props.basement.minSkillsToLose || 0,
      maxSkillsToLose: props.basement.maxSkillsToLose || 0,
      minTattoosToLose: props.basement.minTattoosToLose || 0,
      maxTattoosToLose: props.basement.maxTattoosToLose || 0,
      minEquippedItemsToLose: props.basement.minEquippedItemsToLose || 0,
      maxEquippedItemsToLose: props.basement.maxEquippedItemsToLose || 0,
      minRoomsToLose: props.basement.minRoomsToLose || 0,
      maxRoomsToLose: props.basement.maxRoomsToLose || 0,
      searchCooldown: props.basement.searchCooldown || 0,
      searchOtherPlayersCooldown: props.basement.searchOtherPlayersCooldown || 0,
      combatProcessingCooldown: props.basement.combatProcessingCooldown || 0,
      exitBasementCooldown: props.basement.exitBasementCooldown || 0,
      criminalEncounterChance: props.basement.criminalEncounterChance || 0,
      otherPlayerEncounterChance: props.basement.otherPlayerEncounterChance || 0,
      eventExpirationTime: props.basement.eventExpirationTime || 0,
      searchEnergyCost: props.basement.searchEnergyCost || 0,
      searchOtherPlayersEnergyCost: props.basement.searchOtherPlayersEnergyCost || 0,
      requiredSearchesForCurrency: props.basement.requiredSearchesForCurrency || 1,
    });

    const totalChance = computed(() => editedBasement.value.criminals.reduce((sum, c) => sum + (c.chance || 0), 0));
    const remainingChance = computed(() => 100 - totalChance.value);

    const showBackToTop = ref(false);

    const tabs = [
      { id: 'basic', name: 'ข้อมูลพื้นฐาน' },
      { id: 'criminals', name: 'อาชญากรในชั้นใต้ดิน' },
      { id: 'rewards', name: 'รางวัล' },
    ];
    const currentTab = ref('basic');

    const switchTab = (tabId) => {
      currentTab.value = tabId;
      selectedItemType.value = '';
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
      showBackToTop.value = false;
    };

    const normalizeChances = () => {
      const currentTotal = totalChance.value;
      if (currentTotal === 0) {
        const baseChance = Math.floor(100 / editedBasement.value.criminals.length);
        let remaining = 100 - baseChance * editedBasement.value.criminals.length;
        editedBasement.value.criminals.forEach((criminal, index) => {
          criminal.chance = baseChance + (index < remaining ? 1 : 0);
        });
      } else {
        const scaleFactor = 100 / currentTotal;
        const scaledChances = editedBasement.value.criminals.map((criminal) => (criminal.chance || 0) * scaleFactor);
        const flooredChances = scaledChances.map((chance) => Math.floor(chance));
        const totalFloored = flooredChances.reduce((sum, chance) => sum + chance, 0);
        const remaining = 100 - totalFloored;
        const fractionalParts = scaledChances.map((chance, index) => ({
          index,
          fractional: chance - flooredChances[index],
        })).sort((a, b) => b.fractional - a.fractional);
        for (let i = 0; i < remaining; i++) {
          flooredChances[fractionalParts[i].index]++;
        }
        editedBasement.value.criminals.forEach((criminal, index) => {
          criminal.chance = flooredChances[index];
        });
      }
    };

    const addCriminal = () => {
      const index = editedBasement.value.criminals.length;
      editedBasement.value.criminals.push({ name: '', chance: 0 });
      criminalLoadingStates.value[index] = false;
    };

    const removeCriminal = (index) => {
      if (editedBasement.value.criminals.length > 1) {
        editedBasement.value.criminals.splice(index, 1);
        delete criminalLoadingStates.value[index];
        const newCriminalLoadingStates = {};
        Object.keys(criminalLoadingStates.value).forEach((key) => {
          const newKey = parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
          newCriminalLoadingStates[newKey] = criminalLoadingStates.value[key];
        });
        criminalLoadingStates.value = newCriminalLoadingStates;
      }
    };

    const criminalDetails = ref({});
    const criminalLoadingStates = ref({});
    const criminalShowDetailsAll = ref(false);

    const toggleAllCriminals = () => {
      criminalShowDetailsAll.value = !criminalShowDetailsAll.value;
      localStorage.setItem(`criminal_details_all_${props.basement.id}`, criminalShowDetailsAll.value.toString());
    };

    const fetchCriminalDetails = async (criminalName, index) => {
      if (!criminalName) {
        delete criminalDetails.value[criminalName];
        criminalLoadingStates.value[index] = false;
        return;
      }
      criminalLoadingStates.value[index] = true;
      try {
        const criminalDoc = await projectFirestore.collection('gameCriminals').doc(criminalName).get();
        criminalDetails.value[criminalName] = criminalDoc.exists ? criminalDoc.data() : null;
      } catch (error) {
        console.error(`Error fetching criminal ${criminalName}:`, error);
        criminalDetails.value[criminalName] = null;
      } finally {
        criminalLoadingStates.value[index] = false;
      }
    };

    watch(
      () => editedBasement.value.criminals,
      (newCriminals) => {
        newCriminals.forEach((criminal, index) => {
          if (criminal.name && !criminalDetails.value[criminal.name] && !criminalLoadingStates.value[index]) {
            fetchCriminalDetails(criminal.name, index);
          }
        });
      },
      { deep: true }
    );

    const wearableItemTypes = [
      'หมวก',
      'แว่นตา',
      'เสื้อ',
      'เสื้อคลุม',
      'เกราะ',
      'ปลอกแขน',
      'ถุงมือ',
      'กางเกง',
      'สนับแข้ง',
      'รองเท้า',
      'เข็มขัด',
      'สร้อยคอ',
      'แหวน',
      'อาวุธ',
      'อาวุธสำรอง',
      'ตะกรุด',
      'ผ้ายันต์',
      'สายสิญจน์'
    ];
    const generalItemTypes = ['ทั่วไป', 'อาหาร', 'แผนที่', 'หนังสือ'];

    const selectedItemType = ref('');
    const filteredRewards = computed(() => {
      if (!selectedItemType.value) return editedBasement.value.rewards;
      return editedBasement.value.rewards.filter(reward => {
        const item = itemDetails.value[reward.itemName];
        if (!item) return false;
        if (selectedItemType.value === 'wearable') return wearableItemTypes.includes(item.type);
        if (selectedItemType.value === 'book') return item.type === 'หนังสือ';
        if (selectedItemType.value === 'general') return item.type === 'ทั่วไป';
        if (selectedItemType.value === 'food') return item.type === 'อาหาร';
        if (selectedItemType.value === 'map') return item.type === 'แผนที่';
        if (selectedItemType.value === 'heal') return item.type === 'รักษา';
        return false;
      });
    });

    const wearableItemsCount = computed(() => {
      return filteredRewards.value.filter((reward) => {
        const item = itemDetails.value[reward.itemName];
        return item && wearableItemTypes.includes(item.type);
      }).length;
    });

    const generalItemsCount = computed(() => {
      return filteredRewards.value.filter((reward) => {
        const item = itemDetails.value[reward.itemName];
        return item && generalItemTypes.includes(item.type);
      }).length;
    });

    const itemDetails = ref({});
    const loadingStates = ref({});
    const rewardShowDetailsAll = ref(false);

    const addReward = () => {
      const index = editedBasement.value.rewards.length;
      editedBasement.value.rewards.push({ itemName: '', dropChance: 0 });
      loadingStates.value[index] = false;
    };

    const removeReward = (index) => {
      editedBasement.value.rewards.splice(index, 1);
      delete loadingStates.value[index];
      const newLoadingStates = {};
      Object.keys(loadingStates.value).forEach((key) => {
        const newKey = parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
        newLoadingStates[newKey] = loadingStates.value[key];
      });
      loadingStates.value = newLoadingStates;
    };

    const toggleAllRewards = () => {
      rewardShowDetailsAll.value = !rewardShowDetailsAll.value;
      localStorage.setItem(`reward_details_all_${props.basement.id}`, rewardShowDetailsAll.value.toString());
    };

    const sortRewards = () => {
      editedBasement.value.rewards.sort((a, b) => (a.dropChance || 0) - (b.dropChance || 0));
      const newLoadingStates = {};
      editedBasement.value.rewards.forEach((_, idx) => {
        newLoadingStates[idx] = loadingStates.value[idx] || false;
      });
      loadingStates.value = newLoadingStates;
    };

    const fetchItemDetails = async (itemName, index) => {
      if (!itemName) {
        delete itemDetails.value[itemName];
        loadingStates.value[index] = false;
        return;
      }
      loadingStates.value[index] = true;
      try {
        const itemDoc = await projectFirestore.collection('gameItems').doc(itemName).get();
        itemDetails.value[itemName] = itemDoc.exists ? itemDoc.data() : null;
      } catch (error) {
        console.error(`Error fetching item ${itemName}:`, error);
        itemDetails.value[itemName] = null;
      } finally {
        loadingStates.value[index] = false;
      }
    };

    const getStars = (rarity) => {
      const numStars = Number(rarity) || 0;
      return '★'.repeat(numStars);
    };

    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };

    watch(
      () => editedBasement.value.rewards,
      (newRewards) => {
        newRewards.forEach((reward, index) => {
          if (reward.itemName && !itemDetails.value[reward.itemName] && !loadingStates.value[index]) {
            fetchItemDetails(reward.itemName, index);
          }
        });
      },
      { deep: true }
    );

    const saveChanges = async () => {
      if (totalChance.value !== 100) {
        alert('โอกาสการเจออาชญากรรวมกันต้องเท่ากับ 100%');
        return;
      }

      try {
        const filteredRewards = editedBasement.value.rewards.filter(r => r.itemName && r.dropChance > 0);
        const filteredCriminals = editedBasement.value.criminals.filter(c => c.name && c.chance > 0);
        const basementData = {
          name: editedBasement.value.name,
          imageUrl: editedBasement.value.imageUrl,
          requiredLevel: editedBasement.value.requiredLevel,
          roomRequired: editedBasement.value.roomRequired,
          requiredEnergy: editedBasement.value.requiredEnergy,
          reduceHappy: editedBasement.value.reduceHappy,
          requiredCoins: editedBasement.value.requiredCoins,
          requiredGold: editedBasement.value.requiredGold,
          requiredDiamonds: editedBasement.value.requiredDiamonds,
          requiredCash: editedBasement.value.requiredCash,
          criminals: filteredCriminals.map(c => ({ name: c.name, chance: c.chance || 0 })),
          criminalNames: filteredCriminals.map(c => c.name),
          rewards: filteredRewards,
          rewardItemNames: filteredRewards.map(r => r.itemName),
          itemsToLoseCount: editedBasement.value.itemsToLoseCount,
          skillsToLoseCount: editedBasement.value.skillsToLoseCount,
          tattoosToLoseCount: editedBasement.value.tattoosToLoseCount,
          bagItemsToLoseCount: editedBasement.value.bagItemsToLoseCount,
          rewardSilver: editedBasement.value.rewardSilver,
          rewardGold: editedBasement.value.rewardGold,
          rewardDiamond: editedBasement.value.rewardDiamond,
          allowSearchOtherPlayers: editedBasement.value.allowSearchOtherPlayers,
          minBagItemsToLose: editedBasement.value.minBagItemsToLose,
          maxBagItemsToLose: editedBasement.value.maxBagItemsToLose,
          minSkillsToLose: editedBasement.value.minSkillsToLose,
          maxSkillsToLose: editedBasement.value.maxSkillsToLose,
          minTattoosToLose: editedBasement.value.minTattoosToLose,
          maxTattoosToLose: editedBasement.value.maxTattoosToLose,
          minEquippedItemsToLose: editedBasement.value.minEquippedItemsToLose,
          maxEquippedItemsToLose: editedBasement.value.maxEquippedItemsToLose,
          minRoomsToLose: editedBasement.value.minRoomsToLose,
          maxRoomsToLose: editedBasement.value.maxRoomsToLose,
          searchCooldown: editedBasement.value.searchCooldown,
          searchOtherPlayersCooldown: editedBasement.value.searchOtherPlayersCooldown,
          combatProcessingCooldown: editedBasement.value.combatProcessingCooldown,
          exitBasementCooldown: editedBasement.value.exitBasementCooldown,
          criminalEncounterChance: editedBasement.value.criminalEncounterChance,
          otherPlayerEncounterChance: editedBasement.value.otherPlayerEncounterChance,
          eventExpirationTime: editedBasement.value.eventExpirationTime,
          searchEnergyCost: editedBasement.value.searchEnergyCost,
          searchOtherPlayersEnergyCost: editedBasement.value.searchOtherPlayersEnergyCost,
          requiredSearchesForCurrency: editedBasement.value.requiredSearchesForCurrency,
          createdAt: editedBasement.value.createdAt || new Date(),
        };

        if (editedBasement.value.name !== props.basement.id) {
          const newNameDoc = await projectFirestore.collection('basements').doc(editedBasement.value.name).get();
          if (newNameDoc.exists) {
            alert('ชื่อชั้นนี้มีอยู่แล้ว กรุณาใช้ชื่ออื่น');
            return;
          }
          await projectFirestore.collection('basements').doc(editedBasement.value.name).set(basementData);
          await projectFirestore.collection('basements').doc(props.basement.id).delete();
          localStorage.removeItem(`criminal_details_all_${props.basement.id}`);
          localStorage.removeItem(`reward_details_all_${props.basement.id}`);
        } else {
          await projectFirestore.collection('basements').doc(props.basement.id).update(basementData);
        }

        emit('basementUpdated');
        emit('cancelModal');
      } catch (error) {
        console.error('Error updating basement:', error);
        alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
      }
    };

    const deleteBasement = async () => {
      if (confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบชั้นใต้ดิน "${editedBasement.value.name || 'ไม่มีชื่อ'}"? การกระทำนี้ไม่สามารถย้อนกลับได้`)) {
        try {
          localStorage.removeItem(`criminal_details_all_${props.basement.id}`);
          localStorage.removeItem(`reward_details_all_${props.basement.id}`);
          await projectFirestore.collection('basements').doc(props.basement.id).delete();
          emit('basementDeleted');
          emit('cancelModal');
        } catch (error) {
          console.error('Error deleting basement:', error);
          alert('เกิดข้อผิดพลาดในการลบชั้นใต้ดิน');
        }
      }
    };

    const scrollToTop = () => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const handleScroll = () => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        showBackToTop.value = scrollableDiv.scrollTop > 300;
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        emit('cancelModal');
      } else if (event.key === 'Enter' && !event.target.matches('input, textarea, button')) {
        saveChanges();
      }
    };

    onMounted(async () => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.addEventListener('scroll', handleScroll);
      }
      window.addEventListener('keydown', handleKeydown);

      editedBasement.value.criminals.forEach((_, index) => {
        criminalLoadingStates.value[index] = false;
      });
      editedBasement.value.rewards.forEach((_, index) => {
        loadingStates.value[index] = false;
      });
      criminalShowDetailsAll.value = localStorage.getItem(`criminal_details_all_${props.basement.id}`) === 'true' || false;
      rewardShowDetailsAll.value = localStorage.getItem(`reward_details_all_${props.basement.id}`) === 'true' || false;
      await Promise.all([
        ...editedBasement.value.criminals.map(async (criminal, index) => {
          if (criminal.name) {
            await fetchCriminalDetails(criminal.name, index);
          }
        }),
        ...editedBasement.value.rewards.map(async (reward, index) => {
          if (reward.itemName) {
            await fetchItemDetails(reward.itemName, index);
          }
        }),
      ]);
    });

    onUnmounted(() => {
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('keydown', handleKeydown);
    });

    return {
      editedBasement,
      totalChance,
      remainingChance,
      showBackToTop,
      tabs,
      currentTab,
      switchTab,
      normalizeChances,
      addCriminal,
      removeCriminal,
      toggleAllCriminals,
      criminalDetails,
      criminalLoadingStates,
      criminalShowDetailsAll,
      addReward,
      removeReward,
      toggleAllRewards,
      sortRewards,
      saveChanges,
      deleteBasement,
      scrollToTop,
      wearableItemsCount,
      generalItemsCount,
      selectedItemType,
      filteredRewards,
      itemDetails,
      loadingStates,
      rewardShowDetailsAll,
      getStars,
      handleImageError,
    };
  },
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.scroll-smooth {
  scroll-behavior: smooth;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(79, 70, 229, 0.5) rgba(31, 41, 55, 0.5);
}

.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.5);
  border-radius: 4px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(79, 70, 229, 0.5);
  border-radius: 4px;
  border: 2px solid rgba(31, 41, 55, 0.5);
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgba(79, 70, 229, 0.8);
}

button:hover,
.cursor-pointer:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.bg-glass {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6366f1;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>